import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { PenLine, Eraser, Upload, Loader2, Trash2 } from "lucide-react";

function Pad({ canvasRef, onDraw }) {
  const drawing = useRef(false);
  const pos = (e) => {
    const c = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return [(p.clientX - c.left) * (canvasRef.current.width / c.width), (p.clientY - c.top) * (canvasRef.current.height / c.height)];
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 3.5; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#0B2545";
    ctx.lineTo(...pos(e)); ctx.stroke(); onDraw();
  };
  const end = () => { drawing.current = false; };
  return (
    <canvas ref={canvasRef} width={900} height={280} data-testid="signature-canvas"
      className="w-full h-36 rounded-lg border-2 border-dashed border-slate-300 bg-white touch-none cursor-crosshair"
      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
      onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
  );
}

export function SignatureCard() {
  const canvasRef = useRef(null);
  const [saved, setSaved] = useState("");
  const [license, setLicense] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/account/signature").then(({ data }) => { setSaved(data.signature); setLicense(data.license_number || ""); }).catch(() => {});
  }, []);

  const clear = () => { canvasRef.current.getContext("2d").clearRect(0, 0, 900, 280); setDirty(false); };
  const save = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.put("/account/signature", payload);
      setSaved(data.signature); setLicense(data.license_number || "");
      toast.success("Signature saved — it will appear on your reports and lab requests");
      clear();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not save signature");
    } finally {
      setBusy(false);
    }
  };
  const upload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/image\/(png|jpeg)/.test(f.type) || f.size > 250000) return toast.error("Use a PNG or JPG image under 250 KB");
    const reader = new FileReader();
    reader.onload = () => save({ signature: reader.result, license_number: license });
    reader.readAsDataURL(f);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="signature-card">
      <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><PenLine className="w-4 h-4 text-sky-600" /> Electronic signature</h3>
      <p className="text-sm text-slate-500 mt-1">Added automatically, with your name and today's date, to every referral report and lab request.</p>
      {saved && (
        <div className="mt-4 flex items-center gap-4 rounded-lg bg-slate-50 border border-slate-200 p-3">
          <img src={saved} alt="Saved signature" data-testid="signature-preview" className="h-14 max-w-[220px] object-contain" />
          <Button size="sm" variant="ghost" data-testid="signature-remove-btn" onClick={() => save({ signature: "" })} className="ml-auto text-red-600"><Trash2 className="w-4 h-4 mr-1" /> Remove</Button>
        </div>
      )}
      <p className="text-xs font-medium text-slate-600 mt-4 mb-1.5">{saved ? "Draw a new signature" : "Draw your signature"} (finger or mouse)</p>
      <Pad canvasRef={canvasRef} onDraw={() => setDirty(true)} />
      <div className="mt-4">
        <Label htmlFor="lic">Professional licence number (optional)</Label>
        <Input id="lic" data-testid="license-input" value={license} onChange={(e) => setLicense(e.target.value)} placeholder="e.g. SCFHS 12345" className="mt-1.5" />
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button disabled={busy} data-testid="signature-save-btn" className="bg-sky-600 hover:bg-sky-700"
          onClick={() => save(dirty ? { signature: canvasRef.current.toDataURL("image/png"), license_number: license } : { license_number: license })}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
        <Button variant="outline" onClick={clear} data-testid="signature-clear-btn"><Eraser className="w-4 h-4 mr-1.5" /> Clear</Button>
        <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-slate-200 text-sm font-medium cursor-pointer hover:bg-slate-50" data-testid="signature-upload-label">
          <Upload className="w-4 h-4" /> Upload image
          <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid="signature-upload-input" onChange={upload} />
        </label>
      </div>
    </div>
  );
}
